<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import Icon from '@/components/commons/Icon.vue'
import { formatRelative } from '@/utils/formatRelative'
import type { Vehicle } from '@/api/vehicles'

const props = defineProps<{ vehicle: Vehicle }>()
const emit = defineEmits<{
  edit: [v: Vehicle]
  delete: [v: Vehicle]
}>()

const isOnline = computed(() => {
  const ls = props.vehicle.device?.last_seen_at
  if (!ls) return false
  return Date.now() - new Date(ls).getTime() < 2 * 60 * 1000
})

const lastSeenText = computed(() => formatRelative(props.vehicle.device?.last_seen_at))
</script>

<template>
  <RouterLink
    :to="{ name: 'fleet-vehicle', params: { vehicle_id: vehicle.id } }"
    class="vc-link"
  >
    <article class="vehicle-card hover-lift">
      <header class="vc-head">
        <h3 class="vc-name">{{ vehicle.name }}</h3>
        <span
          class="vc-status"
          :class="isOnline ? 'is-online' : 'is-offline'"
        >
          <span class="vc-status-dot" />
          {{ isOnline ? 'В сети' : 'Не в сети' }}
        </span>
      </header>

      <div class="vc-meta">
        <div class="vc-meta-row">
          <Icon name="clock" :size="13" />
          <span>{{ lastSeenText }}</span>
        </div>
        <div v-if="vehicle.device" class="vc-meta-row" :title="vehicle.device.label ?? ''">
          <Icon name="cpu" :size="13" />
          <span>устройство · {{ vehicle.device.serial }}</span>
        </div>
        <div v-if="!vehicle.is_active" class="vc-meta-row vc-retired">
          <Icon name="archive" :size="13" />
          <span>Выведена</span>
        </div>
      </div>

      <footer class="vc-actions" @click.prevent.stop>
        <button
          type="button"
          class="vc-act"
          data-test="card-edit"
          @click.prevent="emit('edit', vehicle)"
        >
          <Icon name="edit-3" :size="13" /> Изменить
        </button>
        <button
          type="button"
          class="vc-act vc-act-danger"
          data-test="card-delete"
          @click.prevent="emit('delete', vehicle)"
        >
          <Icon name="trash-2" :size="13" /> Удалить
        </button>
      </footer>
    </article>
  </RouterLink>
</template>

<style scoped>
.vc-link { text-decoration: none; color: inherit; display: block; }
.vehicle-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  padding: var(--sp-4);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  box-shadow: var(--shadow-1);
}
.vc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
}
.vc-name {
  margin: 0;
  font-size: var(--fs-md);
  font-weight: 600;
  color: var(--ink);
}
.vc-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--fs-xs);
  padding: 2px 8px;
  border-radius: var(--r-chip);
  font-weight: 500;
}
.vc-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.vc-status.is-online  { color: var(--ok); background: var(--ok-soft); }
.vc-status.is-offline { color: var(--muted); background: var(--bg-2); }
.vc-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--fs-sm);
  color: var(--muted);
}
.vc-meta-row { display: inline-flex; align-items: center; gap: 6px; }
.vc-retired { color: var(--warn); }
.vc-actions {
  display: flex;
  gap: var(--sp-2);
  padding-top: var(--sp-2);
  border-top: 1px solid var(--line-2);
}
.vc-act {
  flex: 1;
  border: 1px solid var(--line);
  background: transparent;
  color: var(--ink-2);
  padding: 6px 10px;
  border-radius: var(--r-ctrl);
  cursor: pointer;
  font-size: var(--fs-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: background 120ms, color 120ms;
}
.vc-act:hover { background: var(--bg-2); }
.vc-act-danger:hover { color: var(--danger); border-color: var(--danger); }
</style>
